/**
 * 用户编辑对话框
 * 包含：基本资料、状态管理、积分调整、会员管理 4 个Tab
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  MonetizationOn as CoinIcon,
  Stars as StarsIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';

import { userApi } from '../../services/mongoUserApi';

// 会员等级配置
const MEMBERSHIP_LEVELS = [
  { value: 'free', label: '免费用户' },
  { value: 'bronze', label: '青铜会员' },
  { value: 'silver', label: '白银会员' },
  { value: 'gold', label: '黄金会员' },
  { value: 'platinum', label: '铂金会员' },
  { value: 'diamond', label: '钻石会员' },
];

// 性别选项
const GENDER_OPTIONS = [
  { value: 'unknown', label: '未知' },
  { value: 'male', label: '男' },
  { value: 'female', label: '女' },
];

const UserEditDialog = ({ open, user, onClose, onSuccess }) => {
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // 基本资料状态
  const [profileData, setProfileData] = useState({
    nickname: '',
    real_name: '',
    gender: 'unknown',
    bio: '',
    phone: '',
  });

  // 状态管理
  const [statusData, setStatusData] = useState({
    is_active: true,
    is_banned: false,
    ban_reason: '',
  });

  // 积分调整
  const [pointsData, setPointsData] = useState({
    operation: 'add', // add | spend
    points: 0,
    source: 'admin_grant',
    title: '管理员调整',
    reason: '管理员调整',
  });

  // 会员管理
  const [membershipData, setMembershipData] = useState({
    level: 'free',
    days: 30,
    operator: 'admin',
  });

  // 加载用户数据
  useEffect(() => {
    if (open && user) {
      setProfileData({
        nickname: user.profile?.nickname || '',
        real_name: user.profile?.real_name || '',
        gender: user.profile?.gender || 'unknown',
        bio: user.profile?.bio || '',
        phone: user.profile?.phone || '',
      });
      setStatusData({
        is_active: user.status?.is_active !== false,
        is_banned: user.status?.is_banned === true,
        ban_reason: user.status?.ban_reason || '',
      });
      setMembershipData({
        level: user.membership?.level || 'free',
        days: 30,
        operator: 'admin',
      });
      setTabValue(0);
      setMessage(null);
    }
  }, [open, user]);

  // 提示消息
  const showMessage = (severity, text) => {
    setMessage({ severity, text });
    if (severity === 'success') {
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 切换Tab
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setMessage(null);
  };

  // 保存基本资料
  const handleSaveProfile = async () => {
    if (!user?._id) return;
    setSaving(true);
    try {
      await userApi.updateProfile(user._id, profileData);
      showMessage('success', '基本资料更新成功');
      onSuccess && onSuccess();
    } catch (err) {
      showMessage('error', '更新失败：' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 保存状态
  const handleSaveStatus = async () => {
    if (!user?._id) return;
    if (statusData.is_banned && !statusData.ban_reason.trim()) {
      showMessage('error', '请填写封禁原因');
      return;
    }
    setSaving(true);
    try {
      await userApi.updateStatus(user._id, statusData);
      showMessage('success', '用户状态更新成功');
      onSuccess && onSuccess();
    } catch (err) {
      showMessage('error', '状态更新失败：' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 调整积分
  const handleAdjustPoints = async () => {
    if (!user?._id) return;
    if (!pointsData.points || pointsData.points <= 0) {
      showMessage('error', '请输入大于0的积分数量');
      return;
    }
    setSaving(true);
    try {
      if (pointsData.operation === 'add') {
        await userApi.addPoints(user._id, {
          points: parseInt(pointsData.points),
          source: pointsData.source,
          title: pointsData.title,
        });
        showMessage('success', `成功增加 ${pointsData.points} 积分`);
      } else {
        await userApi.spendPoints(user._id, {
          points: parseInt(pointsData.points),
          reason: pointsData.reason,
        });
        showMessage('success', `成功扣除 ${pointsData.points} 积分`);
      }
      // 重置积分输入
      setPointsData({ ...pointsData, points: 0 });
      onSuccess && onSuccess();
    } catch (err) {
      showMessage('error', '积分操作失败：' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 开通会员
  const handleGrantMembership = async () => {
    if (!user?._id) return;
    if (!membershipData.level) {
      showMessage('error', '请选择会员等级');
      return;
    }
    if (membershipData.level !== 'free' && (!membershipData.days || membershipData.days <= 0)) {
      showMessage('error', '请输入有效的天数');
      return;
    }
    setSaving(true);
    try {
      await userApi.grantMembership(user._id, {
        level: membershipData.level,
        days: parseInt(membershipData.days),
        operator: membershipData.operator || 'admin',
      });
      showMessage('success', `会员开通成功`);
      onSuccess && onSuccess();
    } catch (err) {
      showMessage('error', '开通失败：' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {/* 标题栏 */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48 }}>
              {user.profile?.avatar ? (
                <img src={user.profile.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <PersonIcon />
              )}
            </Avatar>
            <Box>
              <Typography variant="h6">编辑用户</Typography>
              <Typography variant="body2" color="text.secondary">
                @{user.username} · {user.email}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* 用户当前状态摘要 */}
      <Box sx={{ px: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`积分: ${user.points?.balance?.toLocaleString() || 0}`}
            icon={<CoinIcon />}
            color="warning"
            variant="outlined"
          />
          <Chip
            size="small"
            label={
              user.tier?.current_label
                ? `成长: ${user.tier.current_label}`
                : `成长: Lv.${user.points?.growth_level || 1}`
            }
            variant="outlined"
          />
          <Chip
            size="small"
            label={`会员: ${MEMBERSHIP_LEVELS.find(l => l.value === user.membership?.level)?.label || '免费'}`}
            icon={<StarsIcon />}
            variant="outlined"
          />
          <Chip
            size="small"
            label={user.status?.is_banned ? '已封禁' : (user.status?.is_active === false ? '已禁用' : '正常')}
            color={user.status?.is_banned ? 'error' : (user.status?.is_active === false ? 'default' : 'success')}
          />
        </Box>
      </Box>

      <Divider />

      {/* Tab栏 */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
      >
        <Tab icon={<PersonIcon />} iconPosition="start" label="基本资料" />
        <Tab icon={<BlockIcon />} iconPosition="start" label="状态管理" />
        <Tab icon={<CoinIcon />} iconPosition="start" label="积分调整" />
        <Tab icon={<StarsIcon />} iconPosition="start" label="会员管理" />
      </Tabs>

      <DialogContent sx={{ pt: 3, minHeight: 360 }}>
        {/* 提示消息 */}
        {message && (
          <Alert
            severity={message.severity}
            sx={{ mb: 2 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* Tab 1: 基本资料 */}
        {tabValue === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="昵称"
                value={profileData.nickname}
                onChange={(e) => setProfileData({ ...profileData, nickname: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="真实姓名"
                value={profileData.real_name}
                onChange={(e) => setProfileData({ ...profileData, real_name: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>性别</InputLabel>
                <Select
                  value={profileData.gender}
                  onChange={(e) => setProfileData({ ...profileData, gender: e.target.value })}
                  label="性别"
                >
                  {GENDER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="手机号"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="个人简介"
                multiline
                rows={3}
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                size="small"
                placeholder="一句话介绍自己..."
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving}
                fullWidth
              >
                保存基本资料
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Tab 2: 状态管理 */}
        {tabValue === 1 && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={statusData.is_active}
                    onChange={(e) => setStatusData({ ...statusData, is_active: e.target.checked })}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">账号激活状态</Typography>
                    <Typography variant="caption" color="text.secondary">
                      关闭后用户将无法登录
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={statusData.is_banned}
                    onChange={(e) => setStatusData({ ...statusData, is_banned: e.target.checked })}
                    color="error"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" color={statusData.is_banned ? 'error' : 'text.primary'}>
                      封禁账号
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      封禁后用户将无法使用任何功能
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {statusData.is_banned && (
              <TextField
                fullWidth
                label="封禁原因 *"
                multiline
                rows={3}
                value={statusData.ban_reason}
                onChange={(e) => setStatusData({ ...statusData, ban_reason: e.target.value })}
                size="small"
                placeholder="请说明封禁原因..."
                sx={{ mb: 2 }}
                error={statusData.is_banned && !statusData.ban_reason.trim()}
                helperText={statusData.is_banned && !statusData.ban_reason.trim() ? '封禁原因不能为空' : ''}
              />
            )}

            <Button
              variant="contained"
              color={statusData.is_banned ? 'error' : 'primary'}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveStatus}
              disabled={saving}
              fullWidth
              sx={{ mt: 2 }}
            >
              保存状态变更
            </Button>
          </Box>
        )}

        {/* Tab 3: 积分调整 */}
        {tabValue === 2 && (
          <Box>
            <Box sx={{ p: 2, bgcolor: 'warning.lighter', borderRadius: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CoinIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">当前积分余额</Typography>
                <Typography variant="h5" fontWeight={700} color="warning.dark">
                  {user.points?.balance?.toLocaleString() || 0}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>操作类型</InputLabel>
                  <Select
                    value={pointsData.operation}
                    onChange={(e) => setPointsData({ ...pointsData, operation: e.target.value })}
                    label="操作类型"
                  >
                    <MenuItem value="add">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon fontSize="small" color="success" /> 增加积分
                      </Box>
                    </MenuItem>
                    <MenuItem value="spend">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RemoveIcon fontSize="small" color="error" /> 扣除积分
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="积分数量"
                  type="number"
                  value={pointsData.points}
                  onChange={(e) => setPointsData({ ...pointsData, points: e.target.value })}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CoinIcon color="warning" fontSize="small" />
                      </InputAdornment>
                    ),
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={pointsData.operation === 'add' ? '增加原因' : '扣除原因'}
                  value={pointsData.operation === 'add' ? pointsData.title : pointsData.reason}
                  onChange={(e) => {
                    if (pointsData.operation === 'add') {
                      setPointsData({ ...pointsData, title: e.target.value });
                    } else {
                      setPointsData({ ...pointsData, reason: e.target.value });
                    }
                  }}
                  size="small"
                  placeholder="请填写操作原因，将记录到积分流水"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color={pointsData.operation === 'add' ? 'success' : 'error'}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> :
                    (pointsData.operation === 'add' ? <AddIcon /> : <RemoveIcon />)}
                  onClick={handleAdjustPoints}
                  disabled={saving}
                  fullWidth
                >
                  {pointsData.operation === 'add' ? '确认增加积分' : '确认扣除积分'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 4: 会员管理 */}
        {tabValue === 3 && (
          <Box>
            <Box sx={{ p: 2, bgcolor: 'primary.lighter', borderRadius: 2, mb: 3 }}>
              <Typography variant="caption" color="text.secondary">当前会员状态</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                <StarsIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6">
                  {MEMBERSHIP_LEVELS.find(l => l.value === user.membership?.level)?.label || '免费用户'}
                </Typography>
                {user.membership?.expire_at && user.membership?.level !== 'free' && (
                  <Typography variant="body2" color="text.secondary">
                    到期时间：{new Date(user.membership.expire_at).toLocaleDateString('zh-CN')}
                  </Typography>
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>会员等级</InputLabel>
                  <Select
                    value={membershipData.level}
                    onChange={(e) => setMembershipData({ ...membershipData, level: e.target.value })}
                    label="会员等级"
                  >
                    {MEMBERSHIP_LEVELS.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="开通天数"
                  type="number"
                  value={membershipData.days}
                  onChange={(e) => setMembershipData({ ...membershipData, days: e.target.value })}
                  size="small"
                  disabled={membershipData.level === 'free'}
                  InputProps={{ inputProps: { min: 1, max: 3650 } }}
                  helperText="付费会员的有效天数"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[7, 30, 90, 180, 365].map((d) => (
                    <Chip
                      key={d}
                      label={`${d}天`}
                      size="small"
                      onClick={() => setMembershipData({ ...membershipData, days: d })}
                      color={membershipData.days == d ? 'primary' : 'default'}
                      disabled={membershipData.level === 'free'}
                      variant={membershipData.days == d ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="操作员"
                  value={membershipData.operator}
                  onChange={(e) => setMembershipData({ ...membershipData, operator: e.target.value })}
                  size="small"
                  placeholder="操作员标识，记录到日志"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <StarsIcon />}
                  onClick={handleGrantMembership}
                  disabled={saving}
                  fullWidth
                >
                  确认开通会员
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined">
          关闭
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditDialog;
